<script src="../assets/kurento-utils.js"></script>
<template>
    <b-aspect aspect="16:9" class="video">
        <LoadingSpinner v-if="streamLoading" busy variant="secondary" text="Loading Camera..."
                        text-class="font-xs text-muted"></LoadingSpinner>
        <template>
            <video ref="video_window" autoplay width="100%"></video>
            <div class="indicators">
                <i class="fa-solid fa-xl fa-circle pr-1 text-danger indicator-recording" v-if="recording"></i>
                <i class="fa-solid fa-xl fa-signal-stream-slash mr-1 text-danger indicator-recording" v-else-if="error"></i>
                <i class="fa-solid fa-xl fa-arrow-right-arrow-left fa-flip mr-1 text-warning indicator-recording" v-else-if="mode == 'video'"></i>
                <b-badge pill :variant="statusBadgeVariant" v-if="mode === 'video'">{{ status }}</b-badge>
            </div>
            <div class="buttons">
                <b-dropdown size="sm" class="m-md-2">
                    <template v-slot:button-content>
                        <i class="fa-regular fa-video"></i>
                    </template>
                    <b-dropdown-item :active="currentCamera === 'overheadCamera'" @click="setCamera('overheadCamera')">
                        Use Overhead Camera
                    </b-dropdown-item>
                    <b-dropdown-item :active="currentCamera === 'scaleCamera'" @click="setCamera('scaleCamera')">Use
                        Scale Camera
                    </b-dropdown-item>
                    <b-dropdown-divider></b-dropdown-divider>
                    <b-dropdown-item to="/profile/settings" target="_blank"><i class="fa-duotone fa-cog"></i> Open Settings <i
                        class="fa-regular fa-external-link fa-xs ml-auto"></i></b-dropdown-item>
                    <b-dropdown-item @click="takePhoto"><i class="fa-duotone fa-image-polaroid"></i> Take Photo
                    </b-dropdown-item>
                    <b-dropdown-divider></b-dropdown-divider>
                    <b-dropdown-item @click="dropMedia"><i class="fa-duotone fa-video-slash"></i> Disconnect Camera
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </template>
    </b-aspect>

</template>

<script>
import LoadingSpinner from '@/components/LoadingSpinner';

require('../assets/kurento-utils.js');

const MEDIA_WEB_SOCKET = 'wss://webrtc.beyond79.com:8443/b79';

// Connection timeout timer
const CONNECTION_TIMEOUT = 55000;
const PING_INTERVAL = 5000;

// States
const INIT = 0;
const CONNECTED = 1;
const CONNECTING = 2;
const ERROR = 3;
const STOPPED = 4;
const RETRYING = 5;

// Error codes
const CONNECTION_ERROR = 1;
const VIDEO_STREAM_ERROR = 2;
const VIDEO_RECORD_ERROR = 3;
const VIDEO_SAVE_ERROR = 4;
const LOST_VIDEO = 5;
const WS_CONNECTION_ERROR = 6;

export default {
    name: 'VideoStreamerControl',
    components: {
        LoadingSpinner,
    },
    data() {
        return {
            settings: null,
            currentCamera: null,
            recording: false,
            error: false,
            stream: null,
            track: null,
            tracks: null,
            videoWindow: null,
            streamLoading: false,
            canRecord: false,

            // Kurento WS Global variables
            ws: null,
            webRtcPeer: null,
            state: null,
            status: null,
            statusBadgeVariant: 'secondary',
            timeout: null,
            timer: null,
            ping: null,
            networkTimeout: null,
            autoInit: true,
            retry: null,
            retryCount: 0,
            retryLimit: 2,
            video_data: {
                order_id: this.orderId,
                type: this.videoType,
            },
        };
    },
    props: {
        camera: {
            type: String,
            default: 'overheadCamera',
        },
        mode: {
            type: String,
            default: 'video',
        },
        videoType: {
            type: String,
            default: 'process',
        },
        autoRecord: {
            type: Boolean,
            default: false,
        },
        orderId: {
            type: Number,
        }
    },
    created() {
        this.settings = JSON.parse(localStorage.getItem('settings') || {});
        this.currentCamera = this.camera;
        this.getMedia();
    },
    mounted() {
        this.videoWindow = this.$refs.video_window;
    },
    beforeDestroy() {
        this.dropMedia();
    },
    destroyed() {
        this.stop();
        if (!this.webRtcPeer) {
            this.ws.close();
        }
    },
    methods: {
        async getMedia() {
            this.streamLoading = true;
            navigator.mediaDevices.getUserMedia({
                video: {
                    deviceId: this.settings[this.currentCamera],
                    width: 2048,
                    height: 1536,
                }
            })
                .then((stream) => {
                    this.stream = stream;
                    this.track = this.stream.getVideoTracks()[0];
                    this.tracks = this.stream.getTracks();
                    this.videoWindow.srcObject = this.stream;
                    this.videoWindow.onloadedmetadata = () => {
                        this.videoWindow.play();
                        this.streamLoading = false;

                        if (this.mode === 'video') {
                            this.canRecord = true;
                            this.kurentoRun();
                        }
                    };
                }).catch(error => {
                alert(error);
            });
        },
        async dropMedia() {
            if (this.tracks) {
                this.tracks.forEach(function (track) {
                    track.stop();
                });
            }
            this.videoWindow.srcObject = null;
            this.track = null;
            this.tracks = null;
            this.stream = null;
        },
        async setCamera(cameraName) {
            await this.dropMedia();
            this.currentCamera = cameraName;
            await this.getMedia();
        },
        async takePhoto(type = null) {
            let imageCapture = new ImageCapture(this.track);
            imageCapture.takePhoto().then((blob) => {
                this.$emit('photo-taken', blob, type);
            }).catch(function (error) {
                console.log('takePhoto() error: ', error);
            });
        },
        startRecording() {
            this.recording = true;
            this.rec();
        },
        stopRecording() {
            this.recording = false;
            this.stop();
        },


        // KURENTO
        kurentoRun() {
            /*
             *  Events and Init
             */
            console.log('Run Kurento Init ...');
            this.status = 'Connecting to video server...';
            this.statusBadgeVariant = 'secondary';
            this.error = false;

            window.onbeforeunload = () => {
                this.ws.close();
            };

            this.setState(INIT);
            // Start the WebSocket connection
            this.wsInit();
        },
        wsInit() {
            this.networkTimeout = setTimeout(() => {
                this.onError(WS_CONNECTION_ERROR);
            }, CONNECTION_TIMEOUT);
            this.ws = new WebSocket(MEDIA_WEB_SOCKET);
            this.ws.onopen = () => {
                clearTimeout(this.networkTimeout);
                if (this.autoInit) {
                    this.init();
                }
            };

            this.ws.onerror = () => {
                // if (this.retryCount >= this.retryLimit) {
                    clearTimeout(this.retry);
                    // Set an error state that will be checked on window load
                    console.log('error');
                    this.setState(STOPPED);
                    this.onError(WS_CONNECTION_ERROR);
                // } else {
                //     this.retryCount++;
                //     console.log('retry #' + this.retryCount);
                //     this.setState(RETRYING);
                // }
            };

            this.ws.onclose = function () {
                console.log('Closed');
            };

            this.ws.onmessage = (message) => {
                let parsedMessage = JSON.parse(message.data);

                switch (parsedMessage.id) {
                    case 'startResponse':
                        this.startResponse(parsedMessage);
                        break;

                    case 'error':
                        if (this.state === CONNECTING) {
                            this.setState(INIT);
                        }
                        this.onError(parsedMessage.code, parsedMessage.message);
                        break;

                    case 'iceCandidate':
                        this.webRtcPeer.addIceCandidate(parsedMessage.candidate);
                        break;

                    case 'filename':
                        // $container.trigger('uploaded');
                        // pageMedia.push({
                        //     sub_type: 'process',
                        //     url: 'https://s3.amazonaws.com/beyond79-media/orders/' + pageOrder.id + '/process/' + parsedMessage.filename
                        // });
                        this.ws.close();
                        // reinitialize lightcase for any new images
                        // Vue.nextTick(() => initLightcase());
                        break;

                    case 'state':
                        if (parsedMessage.message === 'ready') {
                            this.setState(CONNECTED);
                        }
                        break;

                    case 'pong':
                        clearTimeout(this.networkTimeout);
                        this.networkTimeout = setTimeout(() => {
                            this.onError(WS_CONNECTION_ERROR);
                        }, PING_INTERVAL + 1000);
                        break;

                    default:
                        if (state === CONNECTING) {
                            this.setState(INIT);
                        }
                        this.onError('Unrecognized message', parsedMessage);
                }
            };
        },
        init() {
            this.setState(INIT);
            clearTimeout(this.timeout);
            clearInterval(this.ping);
            clearTimeout(this.networkTimeout);

            // Proceed to start connection to server
            this.start(this.stream);
        },
        start(videoStream) {
            console.log('Starting video ...', videoStream);

            this.setState(CONNECTING);

            console.log('Creating WebRtcPeer and generating local sdp offer ...');

            let options = {
                videoStream: videoStream,
                onicecandidate: this.onIceCandidate
            };

            let vm = this;
            this.webRtcPeer = kurentoUtils.WebRtcPeer.WebRtcPeerSendonly(options, function(error) {
                if (error) return vm.onError(error);

                this.generateOffer(vm.onOffer)

                //Set a timeout for the connection
                vm.timeout = setTimeout(() => {
                    if (vm.state !== CONNECTED) {
                        vm.onError(CONNECTION_ERROR);
                    }
                }, CONNECTION_TIMEOUT);
            });

        },
        onOffer(error, offerSdp) {
            if (error) return this.onError(error);
            console.info('Invoking SDP offer callback function ' + location.host);

            let message = {
                id: 'start',
                sdpOffer: offerSdp,
                data: this.video_data
            };
            this.sendMessage(message);
        },
        stop() {
            console.log('Stopping video ...');
            this.setState(STOPPED);
            if (this.webRtcPeer) {
                this.webRtcPeer.dispose();
                this.webRtcPeer = null;

                let message = {
                    id: 'stop'
                };
                this.sendMessage(message);
            }
        },
        rec() {
            if (this.webRtcPeer && this.state === CONNECTED) {
                console.log('Recording ...');

                var message = {
                    id: 'record',
                    data: this.video_data
                };
                this.sendMessage(message);
            }
        },
        onIceCandidate(candidate) {
            console.log('ICE');
            var message = {
                id: 'onIceCandidate',
                candidate: candidate
            };
            this.sendMessage(message);
        },
        startResponse(message) {
            if (message.sdpAnswer) {
                console.log('SDP answer received from server. Processing ...');
                this.webRtcPeer.processAnswer(message.sdpAnswer);
            } else {
                console.log('SDP message recieved from the server, but the browser did not process it. Check compatability.')
            }
        },
        setState(nextState) {
            console.log('setting state to ' + nextState);
            switch (nextState) {
                case INIT:
                    // $connecting.show();
                    // $ready.hide();
                    // $failed.hide();
                    // $stopped.hide();
                    // $retrying.hide();
                    // $status.text('');
                    // $('.av-record').prop('disabled', true);
                    break;
                case CONNECTING:
                    // $connecting.show();
                    // $ready.hide();
                    // $failed.hide();
                    // $stopped.hide();
                    // $status.text('');
                    // $('.av-record').prop('disabled', true);
                    // $('.av-choose').prop('disabled', false);
                    break;
                case CONNECTED:
                    this.status = 'LIVE';
                    this.statusBadgeVariant = 'success';
                    // $connecting.hide();
                    // $failed.hide();
                    // $ready.show();
                    // $stopped.hide();
                    // $retrying.hide();
                    // $status.text('');
                    // $('.av-record').prop('disabled', false).addClass('next-step');
                    // $('#next_step_message').text('Start video recording.');
                    // $('.av-choose').prop('disabled', false);

                    if (this.canRecord && this.autoRecord) {
                        this.startRecording();
                    }
                    this.ping = setInterval(() => {
                        this.sendMessage({
                            id: 'ping'
                        });
                    }, PING_INTERVAL);
                    this.networkTimeout = setTimeout(() => {
                        this.onError(WS_CONNECTION_ERROR);
                    }, PING_INTERVAL + PING_INTERVAL + CONNECTION_TIMEOUT);
                    break;
                case STOPPED:
                    // $connecting.hide();
                    // $ready.hide();
                    // $failed.hide();
                    // $stopped.show();
                    // $retrying.hide();
                    // $status.text('');
                    // $('.av-record').prop('disabled', true);
                    // $('.av-stop').prop('disabled', true);
                    // $('.timer').hide();
                    clearTimeout(this.timeout);
                    clearInterval(this.ping);
                    clearTimeout(this.networkTimeout);
                    break;
                case RETRYING:
                    // $connecting.hide();
                    // $retrying.show();
                    this.status ='Retrying... Attempt ' + this.retryCount;
                    this.statusBadgeVariant = 'warning';
                    this.error = false;
                    clearTimeout(this.timeout);
                    clearInterval(this.ping);
                    clearTimeout(this.networkTimeout);
                    clearTimeout(this.retry);
                    this.retry = setTimeout( () => {
                        this.wsInit();
                    }, 2000);
                    break;
            }
            this.state = nextState;

            if (this.autoRecord && this.state === CONNECTED) {
                this.startRecording();
            }
        },
        sendMessage(message, cb) {
            let jsonMessage = JSON.stringify(message);
            console.log(jsonMessage);
            this.ws.send(jsonMessage);
            if (typeof cb === 'function') {
                cb();
            }
        },
        restart() {
            this.stop();
            this.init();
        },
        onError(error, message) {
            this.ws.close(4000, 'Timeout');
            clearTimeout(this.timeout);
            clearInterval(this.ping);
            clearTimeout(this.networkTimeout);
            // $connecting.hide();
            // $ready.hide();
            // $stopped.hide();
            // $failed.show();
            // $('.av-record, .av-stop, .timer').hide();
            this.status = 'ERROR';
            this.statusBadgeVariant = 'danger';

            if (this.state === ERROR) {
                return;
            }

            switch (error) {
                case CONNECTION_ERROR:
                    this.status = 'ERROR CONNECTING TO SERVER';
                    this.statusBadgeVariant = 'danger';
                    console.log('CONNECTION_ERROR');
                    // new modal({
                    //     'template': 'error_modal', 'data': {
                    //         'message': 'Unable to connect to video server... please try refreshing the page.<br>' +
                    //             'If the problem persists, please contact Production Support immediately.'
                    //     }
                    // });
                    // $('.proceed a').html('SKIP and Proceed to next step >>');
                    // $('.proceed').show();
                    this.$bvModal.msgBoxOk('Unable to connect to video server... please try refreshing the page.', {
                        titleHtml: '<i class="fa-regular fa-exclamation-triangle fa-2xl fa-fw text-danger"></i> Video Error: <small class="text-danger">CONNECTION_ERROR</small>',
                        size: 'sm',
                        buttonSize: 'sm',
                        okVariant: 'warning',
                        centered: true
                        });
                    this.restart();
                    break;

                case VIDEO_STREAM_ERROR:
                    this.status = 'ERROR STREAMING TO SERVER';
                    this.statusBadgeVariant = 'danger';
                    console.log('VIDEO_STREAM_ERROR');
                    // new modal({
                    //     'template': 'error_modal', 'data': {
                    //         'message': 'Connected, but unable to stream to video server...' +
                    //             'Automatically resetting the connection... please try again.<br>' +
                    //             'If the problem persists, please contact Production Support immediately.'
                    //     }, 'onClose': restart
                    // });
                    this.$bvModal.msgBoxOk('Connected, but unable to stream to video server... Automatically resetting the connection... please try again.', {
                        titleHtml: '<i class="fa-regular fa-exclamation-triangle fa-2xl fa-fw text-danger"></i> Video Error: <small class="text-danger">VIDEO_STREAM_ERROR</small>',
                        size: 'sm',
                        buttonSize: 'sm',
                        okVariant: 'danger',
                        centered: true
                        });
                    this.restart();
                    break;

                case VIDEO_RECORD_ERROR:
                    console.log('VIDEO_RECORD_ERROR');
                    // new modal({
                    //     'template': 'error_modal', 'data': {
                    //         'message': 'Video failed to record.<br><br>' +
                    //             message +
                    //             '<br><br>Automatically resetting the connection... please try again.<br>' +
                    //             'If the problem persists, please contact Production Support immediately.'
                    //     }, 'onClose': restart
                    // });
                    this.$bvModal.msgBoxOk('Video failed to record. Automatically resetting the connection... please try again.', {
                        titleHtml: '<i class="fa-regular fa-exclamation-triangle fa-2xl fa-fw text-danger"></i> Video Error: <small class="text-danger">VIDEO_RECORD_ERROR</small>',
                        size: 'sm',
                        buttonSize: 'sm',
                        okVariant: 'danger',
                        centered: true
                        });
                    this.restart();
                    break;

                case VIDEO_SAVE_ERROR:
                    console.log('VIDEO_SAVE_ERROR');
                    // new modal({
                    //     'template': 'error_modal', 'data': {
                    //         'message': 'The video file did not save properly.<br>' +
                    //             'Please try again.<br>' +
                    //             'If the problem persists, please contact Production Support immediately.'
                    //     }, 'onClose': restart
                    // });
                    this.$bvModal.msgBoxOk('The video file did not save properly. Please refresh the page to try again.', {
                        titleHtml: '<i class="fa-regular fa-exclamation-triangle fa-2xl fa-fw text-danger"></i> Video Error: <small class="text-danger">VIDEO_SAVE_ERROR</small>',
                        size: 'sm',
                        buttonSize: 'sm',
                        okVariant: 'danger',
                        centered: true
                        });
                    break;

                case LOST_VIDEO:
                    console.log('LOST_VIDEO');
                    // new modal({
                    //     'template': 'error_modal', 'data': {
                    //         'message': 'The video stream was terminated.<br>' +
                    //             'All previous video was recorded and saved up to the point of loss.<br>' +
                    //             'You may refresh to resume recording if needed, or click Proceed below to continue on.'
                    //     }
                    // });
                    // $('.proceed').show();
                    this.$bvModal.msgBoxOk('The video stream was interrupted. Automatically resetting the connection... please try again.', {
                        titleHtml: '<i class="fa-regular fa-exclamation-triangle fa-2xl fa-fw text-danger"></i> Video Error: <small class="text-danger">LOST_VIDEO</small>',
                        size: 'sm',
                        buttonSize: 'sm',
                        okVariant: 'danger',
                        centered: true
                        });
                    this.restart();
                    break;

                case WS_CONNECTION_ERROR:
                    this.status = 'ERROR CONNECTING TO SERVER';
                    this.statusBadgeVariant = 'danger';
                    this.error = true;
                    console.log('WS_CONNECTION_ERROR');
                    // new modal({
                    //     'template': 'error_modal', 'data': {
                    //         'message': 'Unable to initialize a WebSocket connection to video server... please try refreshing the page.<br><br>' +
                    //             'If the problem persists, please contact Production Support immediately.'
                    //     }
                    // });
                    if (this.webRtcPeer) {
                        this.$bvModal.msgBoxOk('Unable to connect to video server... please try refreshing the page.', {
                            titleHtml: '<i class="fa-regular fa-exclamation-triangle fa-2xl fa-fw text-danger"></i> Video Error: <small class="text-danger">WS_CONNECTION_ERROR</small>',
                            size: 'sm',
                            buttonSize: 'sm',
                            okVariant: 'danger',
                            centered: true
                            });
                        this.webRtcPeer.dispose();
                        this.webRtcPeer = null;
                    }
                    // this.restart();
                    // $('.proceed a').html('SKIP and Proceed to next step >>');
                    // $('.proceed').show();
                    break;

                default:
                    console.log(error);
                    break;
            }

            this.setState(ERROR);
        },


    },
    watch: {
        order(value) {
            if (value != null) {
                if (this.mode === 'video') {
                    this.canRecord = true;
                    this.kurentoRun();
                }
            }
        },

    },
};
</script>

<style scoped>
.video {
    position: relative;
    width: 100%;
}

.video video {
    display: block;
}

.video .indicators {
    position: absolute;
    top: 4px;
    left: 4px;
    z-index: 1;
    width: 100%;
    text-align: left;
    display: block;
}

.video .buttons {
    position: absolute;
    bottom: 0;
    z-index: 1;
    width: 100%;
    text-align: right;
    display: none;
}

.video:hover .buttons {
    display: block;
}
</style>